import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pause, Play } from 'lucide-react';
import { apiGet, apiPost, apiDelete } from '../../lib/api';

const inputCls = 'w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rozops-blue/20 focus:border-rozops-blue';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const emptyForm = {
  customer_id: '',
  product_id: '',
  quantity: 1,
  frequency: 'daily',
  weekdays: [0, 1, 2, 3, 4, 5, 6],
  schedule_config: { interval_days: 2, day_of_month: 1 },
  start_date: '',
  end_date: '',
  delivery_time: 'morning',
};

function scheduleSummary(subscription) {
  const frequency = subscription.frequency || 'daily';
  let config = subscription.schedule_config || {};
  if (typeof config === 'string') {
    try { config = JSON.parse(config); } catch { config = {}; }
  }
  if (frequency === 'daily') return 'Daily';
  if (frequency === 'alternate_day') return `Every ${config.interval_days || 2} days`;
  if (frequency === 'monthly') return `Monthly on day ${config.day_of_month || config.days?.[0] || 'set'}`;
  return `Weekdays: ${(subscription.weekdays || []).map(d => WEEKDAYS[d]).join(', ')}`;
}

export default function Subscriptions() {
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const [s, c, p] = await Promise.all([apiGet('/merchant/subscriptions'), apiGet('/merchant/customers'), apiGet('/merchant/products')]);
    setList(s.data || []);
    setCustomers(c.data || []);
    setProducts(p.data || []);
  };
  useEffect(() => { load().catch(e => toast.error(e.message)); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.customer_id || !form.product_id) return toast.error('Pick customer and product');
    try {
      const payload = {
        ...form,
        quantity: Number(form.quantity),
        weekdays: form.frequency === 'daily' ? [0, 1, 2, 3, 4, 5, 6] : form.weekdays,
        schedule_config: form.frequency === 'alternate_day'
          ? { interval_days: Number(form.schedule_config.interval_days || 2), anchor_date: form.start_date || undefined }
          : form.frequency === 'monthly'
            ? { day_of_month: Number(form.schedule_config.day_of_month || 1), days: [Number(form.schedule_config.day_of_month || 1)] }
            : {},
      };
      await apiPost('/merchant/subscriptions', payload);
      toast.success('Subscription created');
      setShowAdd(false);
      setForm(emptyForm);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const togglePause = async (s) => {
    const url = s.status === 'active' ? `/merchant/subscriptions/${s.id}/pause` : `/merchant/subscriptions/${s.id}/resume`;
    await apiPost(url);
    toast.success('Updated');
    load();
  };

  const end = async (s) => {
    if (!window.confirm('End this subscription?')) return;
    await apiDelete(`/merchant/subscriptions/${s.id}`);
    toast.success('Ended');
    load();
  };

  const toggleDay = (d) => {
    const days = form.weekdays.includes(d) ? form.weekdays.filter(x => x !== d) : [...form.weekdays, d];
    setForm({ ...form, weekdays: days });
  };

  return (
    <div className="space-y-5" data-testid="subs-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-extrabold text-3xl text-rozops-navy tracking-tight">Subscriptions</h1>
        <button onClick={() => setShowAdd(true)} data-testid="add-sub-btn" className="bg-rozops-blue text-white rounded-xl px-4 py-2.5 font-semibold shadow-soft inline-flex items-center gap-2"><Plus className="w-4 h-4" /> New subscription</button>
      </div>

      <div className="grid gap-3">
        {list.length === 0 && <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center text-rozops-text-muted">No subscriptions yet.</div>}
        {list.map(s => (
          <div key={s.id} className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`sub-${s.id}`}>
            <div>
              <div className="font-heading font-bold text-lg text-rozops-navy">{s.customer?.full_name || '—'}</div>
              <div className="text-sm text-rozops-text-muted">{s.product?.sku ? `${s.product.sku} · ` : ''}{s.product?.name} · {s.quantity} {s.product?.unit}</div>
              <div className="text-xs text-rozops-text-muted mt-1">{scheduleSummary(s)}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${s.status === 'active' ? 'bg-rozops-green-light text-rozops-green' : s.status === 'paused' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-rozops-text-muted'}`}>{s.status}</span>
              <button onClick={() => togglePause(s)} className="rounded-xl border-2 border-rozops-blue-light hover:border-rozops-blue px-3 py-1.5 text-sm font-semibold inline-flex items-center gap-1.5">
                {s.status === 'active' ? <><Pause className="w-3.5 h-3.5" /> Pause</> : <><Play className="w-3.5 h-3.5" /> Resume</>}
              </button>
              <button onClick={() => end(s)} className="text-red-500 text-xs font-semibold px-2">End</button>
            </div>
          </div>
        ))}
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-2xl shadow-medium p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading font-bold text-xl text-rozops-navy mb-4">New subscription</h2>
            <form onSubmit={add} className="space-y-3">
              <select className={inputCls} value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} required data-testid="sub-customer">
                <option value="">Select customer</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
              </select>
              <select className={inputCls} value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} required data-testid="sub-product">
                <option value="">Select product</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.sku ? `${p.sku} · ` : ''}{p.name} (₹{p.default_price}/{p.unit})</option>)}
              </select>
              <input type="number" step="0.5" min="0.5" placeholder="Quantity" className={inputCls} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="sub-qty" />
              <select className={inputCls} value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}>
                <option value="daily">Daily</option>
                <option value="custom_weekdays">Selected weekdays</option>
                <option value="alternate_day">Alternate / every N days</option>
                <option value="monthly">Monthly</option>
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input type="date" className={inputCls} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                <input type="date" className={inputCls} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </div>
              {form.frequency === 'custom_weekdays' && <div>
                <div className="text-xs uppercase tracking-widest font-bold text-rozops-text-muted mb-2">Delivery days</div>
                <div className="flex flex-wrap gap-2">
                  {WEEKDAYS.map((d, i) => (
                    <button key={d} type="button" onClick={() => toggleDay(i)} className={`px-3 py-1.5 rounded-lg text-xs font-bold border-2 ${form.weekdays.includes(i) ? 'bg-rozops-blue border-rozops-blue text-white' : 'bg-white border-gray-200 text-rozops-text-muted'}`}>{d}</button>
                  ))}
                </div>
              </div>}
              {form.frequency === 'alternate_day' && (
                <input type="number" min="1" max="30" className={inputCls} placeholder="Repeat every N days" value={form.schedule_config.interval_days} onChange={(e) => setForm({ ...form, schedule_config: { ...form.schedule_config, interval_days: e.target.value } })} />
              )}
              {form.frequency === 'monthly' && (
                <input type="number" min="1" max="31" className={inputCls} placeholder="Day of month" value={form.schedule_config.day_of_month} onChange={(e) => setForm({ ...form, schedule_config: { ...form.schedule_config, day_of_month: e.target.value } })} />
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 rounded-xl text-rozops-text-muted">Cancel</button>
                <button type="submit" data-testid="sub-submit" className="bg-rozops-blue text-white rounded-xl px-4 py-2 font-semibold">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
